import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Button, Tab, Tabs, useTheme, useMediaQuery, CssBaseline, createTheme, ThemeProvider, CircularProgress, Typography } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next'; // Import i18next
import axios from "axios";
import Coupon from './models/Coupon';
import Header from './components/Header';
import NavigationDrawer from './components/NavigationDrawer';
import CouponsList from './components/CouponsList';
import { LANGUAGES, categories, CATEGORIES } from './constants';
import { colors } from './theme/colors';
import './assets/fonts/stylesheet.css';
import './i18n/config';
import './styles/global.css';
import NoCodesImage from './assets/images/no-results-image.svg';
import { analytics, setUserProperties } from './config/firebase'; 
import { debounce } from 'lodash'; // You may need to install lodash if not already included

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const getStoredLanguage = () => {
  const stored = localStorage.getItem('language');
  return stored && LANGUAGES[stored] ? stored : 'AR';
};

const getStoredCountry = () => {
  return localStorage.getItem('country') || 'Kingdom of Saudi Arabia';
};

const params = new URLSearchParams(window.location.search);
const PLATFORM = params.get("platform");

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedStore, setSelectedStore] = useState(null); // Track the selected store
  const [country, setCountry] = useState(getStoredCountry());
  const [language, setLanguage] = useState(getStoredLanguage());
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null); // Tracks the last document for pagination
  const [hasMore, setHasMore] = useState(true); // Add this state to track if more data exists
  const { t } = useTranslation();

  const INITIAL_LOAD_LIMIT = 25;
  const PAGINATION_LOAD_LIMIT = 5;

  const countryOptions = useMemo(() => [
    { value: 'The Kingdom of Bahrain', label: t('countries.The Kingdom of Bahrain') },
    { value: 'the Arab Republic of Egypt', label: t('countries.The Arab Republic of Egypt') },
    { value: 'The State of Kuwait', label: t('countries.The State of Kuwait') },
    { value: 'Sultanate of Oman', label: t('countries.Sultanate of Oman') },
    { value: 'The State of Qatar', label: t('countries.The State of Qatar') },
    { value: 'Kingdom of Saudi Arabia', label: t('countries.Kingdom of Saudi Arabia') },
    { value: 'The United Arab Emirates', label: t('countries.The United Arab Emirates') }
  ], [t, language]);

  const categoriesWithLabels = useMemo(() => 
    CATEGORIES.map(category => ({
      ...category,
      label: t(`categories.${category.value}`)
    }))
  , [t]);

  const fetchCoupons = useCallback(
    async (isInitialLoad = false) => {
      try {
        if (isInitialLoad) {
          setLoading(true);
          setLastVisible(null);
        } else {
          if (!hasMore) return; // Don't fetch if we know there's no more data
          setLoadingMore(true);
        }

        const response = await axios.post(
          "https://fetchcoupons-o5eqkgosgq-uc.a.run.app",
          {
            country,
            selectedCategoryValue: CATEGORIES[selectedCategory]?.value || "all",
            selectedStore,
            lastVisible,
            isInitialLoad,
            INITIAL_LOAD_LIMIT,
            PAGINATION_LOAD_LIMIT,
          }
        );

        const { coupons: fetchedCoupons, lastVisible: newLastVisible } = response.data;

        // If we got less data than requested or no lastVisible, we've reached the end
        if (!newLastVisible || fetchedCoupons.length < (isInitialLoad ? INITIAL_LOAD_LIMIT : PAGINATION_LOAD_LIMIT)) {
          setHasMore(false);
        } else {
          setLastVisible(newLastVisible);
          setHasMore(true);
        }

        setCoupons((prevCoupons) =>
          isInitialLoad ? fetchedCoupons : [...prevCoupons, ...fetchedCoupons]
        );
      } catch (error) {
        console.error("Error fetching coupons:", error);
        setHasMore(false);
      } finally {
        if (isInitialLoad) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [country, selectedCategory, selectedStore, lastVisible]
  );

  useEffect(() => {
    fetchCoupons(true); // Initial load
  }, [country, selectedCategory, selectedStore]);

  const debouncedFetchMore = useCallback(
    debounce(() => {
      fetchCoupons(false);
    }, 300),
    [fetchCoupons]
  );

  useEffect(() => {
    const rootElement = document.getElementById('root');
    
    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = rootElement;
      
      if (scrollHeight - scrollTop - clientHeight < 100) {
        if (!loading && !loadingMore && hasMore) { // Only fetch more if hasMore is true
          debouncedFetchMore();
        }
      }
    };
    
    // Check on mount and whenever coupons change
    checkScroll();
    
    // Add event listeners to both root and document
    rootElement.addEventListener('scroll', checkScroll, { passive: true });
    document.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('scroll', checkScroll, { passive: true });
    
    // Also check periodically just in case
    const interval = setInterval(checkScroll, 2000);
    
    return () => {
      rootElement.removeEventListener('scroll', checkScroll);
      document.removeEventListener('scroll', checkScroll);
      window.removeEventListener('scroll', checkScroll);
      clearInterval(interval);
      debouncedFetchMore.cancel();
    };
  }, [loading, loadingMore, debouncedFetchMore, coupons, hasMore]); // Add hasMore to dependencies

  useEffect(() => {
    const initializeApp = async () => {
      await i18next.changeLanguage(language);
      document.documentElement.setAttribute('dir', LANGUAGES[language].dir);
      document.body.setAttribute('dir', LANGUAGES[language].dir);
    };
    
    initializeApp();
  }, [language]); // Run on mount and when language changes

  const theme = createTheme({ 
    direction: LANGUAGES[language].dir,
    typography: {
      fontFamily: '"Graphik Arabic", sans-serif',
      allVariants: {
        fontFamily: '"Graphik Arabic", sans-serif',
      },
    },
    palette: {
      primary: colors.primary,
      text: {
        primary: '#000000', // Default text color
        secondary: '#757575', // Placeholder text color
      },
      background: {
        default: '#f5f5f5'
      }
    }
  });

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    var platform_type;

    if (PLATFORM) {
      platform_type = PLATFORM;
    } else {
      platform_type = isMobile ? "mobile_web" : "desktop_web"
    }

    setUserProperties(analytics, {
      platform_type: platform_type
    });

  }, [isMobile]);

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const handleCountryChange = (event) => {
    const newCountry = event.target.value;
    setCountry(newCountry);
    localStorage.setItem('country', newCountry);
  };

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setSelectedCategory(0); // Reset to first category (usually "All")
  };

  const handleCategorySelect = (index) => {
    setSelectedCategory(index);
    setSelectedStore(null); // Clear selected store
  };

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ 
          flexGrow: 1,
          height: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          <Box sx={{ 
            pt: PLATFORM === 'ios_app' ? '45px' : 0,
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: 'primary.main'
          }}>
            <Header
              isMobile={isMobile}
              language={language}
              country={country}
              countryOptions={countryOptions}
              onLanguageChange={handleLanguageChange}
              onCountryChange={handleCountryChange}
              onSelectedStoreChange={handleStoreSelect}
              onDrawerToggle={() => setMobileOpen(!mobileOpen)}
              categories={categoriesWithLabels}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
            />
          </Box>

          {isMobile && (
            <NavigationDrawer
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              language={language}
              country={country}
              countryOptions={countryOptions}
              onLanguageChange={handleLanguageChange}
              onCountryChange={handleCountryChange}
            />
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : coupons.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
              <img
                src={NoCodesImage}
                alt="No codes found"
                style={{ maxWidth: '500px', width: '100%', height: 'auto' }}
              />
              <Typography variant="h6" sx={{ mt: 5, color: 'black' }}>
                {t('labels.noCodesFound')}
              </Typography>
            </Box>
          ) : (
            <>
              <CouponsList 
                isMobile={isMobile}
                coupons={coupons}
                loading={loading}
                loadingMore={loadingMore}
                language={language}
              />
            </>
          )}
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
